import { Injectable } from '@angular/core';
import {
  type ChargeCode,
  type TimeSheet,
  type WorkRole,
  type WorkType,
  type TimeEntry,
} from 'connectwise-rest/dist/Manage/TimeAPI';
import { type Company } from 'connectwise-rest/dist/Manage/CompanyAPI';
import { type Ticket } from 'connectwise-rest/dist/Manage/ServiceAPI';
import { type ProjectTicket } from 'connectwise-rest/dist/Manage/ProjectAPI';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { Agreement } from 'connectwise-rest/dist/Manage/FinanceAPI';
import { rxResource } from '@angular/core/rxjs-interop';
import { format, parseISO } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class ConnectwiseService {
  url: string = environment.apiUrl;
  constructor(private http: HttpClient) { }

  getTimeentries(args: { startDate: Date; endDate?: Date }): Observable<TimeEntry[]> {
    let params = new HttpParams().set('date', args.startDate.toISOString());
    if (args.endDate) {
      params = params.set('endDate', args.endDate.toISOString());
    }
    return this.http.get<TimeEntry[]>(`${this.url}/api/v1/cw/time_entries`, {
      params,
    }).pipe(
      map(entries => entries.filter(e => e.actualHours ? e.actualHours > 0 : false))
    )
  }

  /**
   * Total booked hours per day over a date range, keyed by local `yyyy-MM-dd`.
   */
  getDailyHours(args: { startDate: Date; endDate: Date }): Observable<Record<string, number>> {
    return this.getTimeentries(args).pipe(
      map((entries) =>
        entries.reduce<Record<string, number>>((totals, entry) => {
          if (!entry.timeStart) return totals;
          const key = format(parseISO(entry.timeStart), 'yyyy-MM-dd');
          totals[key] = (totals[key] ?? 0) + (entry.actualHours ?? 0);
          return totals;
        }, {}),
      ),
    );
  }

  getCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.url}/api/v1/cw/companies`, {});
  }

  getWorkRoles(): Observable<WorkRole[]> {
    return this.http.get<WorkRole[]>(`${this.url}/api/v1/cw/work_roles`, {});
  }

  getWorkTypes(): Observable<WorkType[]> {
    return this.http.get<WorkType[]>(`${this.url}/api/v1/cw/work_types`, {});
  }

  getServiceTickets(needle?: string): Observable<any[]> {
    return this.http.get<Ticket[]>(`${this.url}/api/v1/cw/service_tickets`, {
      params: needle ? new HttpParams().set('search', `${needle.trim()}`) : undefined
    });
  }

  getProjectTickets(needle?: string): Observable<any[]> {
    return this.http.get<ProjectTicket[]>(
      `${this.url}/api/v1/cw/project_tickets`,
      {
        params: needle ? new HttpParams().set('search', `${needle.trim()}`) : undefined
      },
    ).pipe(
      map((tickets) => tickets.flatMap((t) => t)),
    );
  }

  getChargeCodes(): Observable<ChargeCode[]> {
    return this.http.get<ChargeCode[]>(
      `${this.url}/api/v1/cw/charge_codes`,
      {},
    );
  }

  getTimesheets(): Observable<TimeSheet[]> {
    return this.http.get<TimeSheet[]>(`${this.url}/api/v1/cw/timesheets`, {});
  }

  public readonly timesheetsResource = rxResource({
    defaultValue: [],
    stream: () => this.getTimesheets()
  });

  submitTimesheet(timesheet: TimeSheet): Observable<TimeSheet> {
    return this.http.post<TimeSheet>(
      `${this.url}/api/v1/cw/timesheets/`,
      timesheet,
    );
  }

  getAgreements(needle?: string): Observable<Agreement[]> {
    return this.http.get<Agreement[]>(`${this.url}/api/v1/cw/agreements`, {
      params: needle ? new HttpParams().set('conditions', `name="${needle}"`) : undefined
    });
  }

  tryGetCompanyFromEmail(email: string): Observable<Company | null> {
    if (email.includes('@')) {
      return this.http
        .post<Company>(`${this.url}/api/v1/cw/search_company_by_email`, {
          email,
        })
        .pipe(catchError(() => of(null)));
    } else {
      return of(null);
    }
  }

  submitTimeEntry(timeEntry: unknown): Observable<TimeEntry> {
    return this.http.post<TimeEntry>(
      `${this.url}/api/v1/cw/time_entries`,
      timeEntry,
    );
  }

  deleteTimeEntry(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.url}/api/v1/cw/time_entries/${id}`);
  }

  reloadTimesheets(): void {
    this.timesheetsResource.reload();
  }
}
